import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field, Formik } from "formik";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import * as Yup from "yup";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { green } from "@mui/material/colors";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import {
  FormikAutocomplete,
  FullWidthField,
} from "@poseidon/ui/components";
import { getUserLabel } from "../../Users/util";
import { useSailors } from "../../Sailors/hooks/useSailors";
import { useCreateSailorAccount } from "./useCreateSailorAccount";
import { useCreateApplication } from "./useCreateApplication";

const newUserSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
});

const existingUserSchema = Yup.object().shape({
  sailor: Yup.object().nullable().required("Select a user"),
});

const initialValues = {
  email: "",
  firstName: "",
  lastName: "",
  sailor: null,
};

function GeneratedPasswordPanel({ password }) {
  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Generated password"
        value={password}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="copy password"
                onClick={() => navigator.clipboard?.writeText(password)}
                edge="end"
              >
                <ContentCopyIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Typography variant="caption" color="text.secondary">
        This password is shown only once — copy it now and share it with the
        user.
      </Typography>
    </Box>
  );
}

export function CreateApplicationStep({ quote, boat, onCancel, onClose }) {
  const navigate = useNavigate();
  const { setToast } = useToastMessage();
  const { createAccount } = useCreateSailorAccount();
  const { createApplication } = useCreateApplication();
  const { sailors, loading: sailorsLoading } = useSailors();

  const [mode, setMode] = useState("new");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(undefined);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const createdSailorId = useRef(null);

  const goToApplication = (id) => {
    onClose();
    navigate(`/applications/${id}`);
  };

  const createApplicationForSailor = async (sailorId) => {
    const appId = await createApplication({ sailorId, quote, boat });
    if (!appId) {
      throw new Error("Failed to create the application. Please retry.");
    }
    setApplicationId(appId);
    setToast("Application created");
    return appId;
  };

  const handleSubmit = async (values) => {
    setError(undefined);
    setBusy(true);
    try {
      if (mode === "existing") {
        const appId = await createApplicationForSailor(values.sailor.id);
        goToApplication(appId);
        return;
      }
      // New user: create the auth account once (skip if a retry).
      if (!createdSailorId.current) {
        const { uid, password } = await createAccount({
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
        });
        createdSailorId.current = uid;
        setGeneratedPassword(password);
      }
      await createApplicationForSailor(createdSailorId.current);
      // Keep the dialog open so the admin can copy the one-time password,
      // then navigate via "Go to application".
    } catch (err) {
      setError(err.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  // New-user result phase: account created, show the one-time password.
  if (mode === "new" && generatedPassword) {
    return (
      <>
        <DialogContent>
          <Alert severity="success">User account created.</Alert>
          <GeneratedPasswordPanel password={generatedPassword} />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {applicationId ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => goToApplication(applicationId)}
            >
              Go to application
            </Button>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Button
                variant="contained"
                color="primary"
                disabled={busy}
                onClick={() => handleSubmit()}
              >
                Retry creating application
              </Button>
              {busy && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    mt: "-12px",
                    ml: "-12px",
                  }}
                />
              )}
            </Box>
          )}
        </DialogActions>
      </>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={mode === "new" ? newUserSchema : existingUserSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <DialogContent>
          <ToggleButtonGroup
            color="primary"
            exclusive
            fullWidth
            size="small"
            value={mode}
            disabled={busy}
            onChange={(_, value) => {
              if (value) {
                setMode(value);
                setError(undefined);
              }
            }}
            sx={{ mb: 1 }}
          >
            <ToggleButton value="new">New user</ToggleButton>
            <ToggleButton value="existing">Existing user</ToggleButton>
          </ToggleButtonGroup>

          {mode === "new" ? (
            <>
              <Field
                component={FullWidthField}
                name="email"
                label="Email"
                margin="normal"
              />
              <Field
                component={FullWidthField}
                name="firstName"
                label="First Name"
                margin="normal"
              />
              <Field
                component={FullWidthField}
                name="lastName"
                label="Last Name"
                margin="normal"
              />
              <Alert severity="warning" sx={{ mt: 2 }}>
                A new user account will be created. A one-time password will be
                generated and shown once — share it with the user so they can
                sign in.
              </Alert>
            </>
          ) : (
            <Field
              component={FormikAutocomplete}
              name="sailor"
              label="User"
              margin="normal"
              loading={sailorsLoading}
              options={sailors}
              getOptionLabel={(option) => getUserLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Box sx={{ position: "relative" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={busy}
            >
              Create Application
            </Button>
            {busy && (
              <CircularProgress
                size={24}
                sx={{
                  color: green[500],
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  mt: "-12px",
                  ml: "-12px",
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Form>
    </Formik>
  );
}
